import { invoke } from "@tauri-apps/api/core";
import { produce } from "immer";
import { isEmpty } from "lodash";
import {
	type ActorRefFrom,
	assign,
	emit,
	enqueueActions,
	fromPromise,
	not,
	or,
	sendParent,
	setup,
} from "xstate";

import {
	type BaseSpec,
	type DocumentMetadata,
	type PageMetadata,
	type PublishSettings,
} from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { isDesktopAppMode } from "~/auth/interop/common";
import { filesystemApi } from "~/state/api/filesystem";
import { giteaApi } from "~/state/api/gitea";
import { type DocumentListItem } from "~/state/types";

import { type AuthenticationMachine } from "../authentication";
import { baseMachine } from "../base/base.machine";
import { gitRepositoryMachine } from "../git/repository/git-repository.machine";
import { createPageMachine } from "../pages/create-page.machine";
import { pageMachine } from "../pages/page.machine";
import { sharingMachine } from "../permissions/sharing.machine";
import { transferMachine } from "../permissions/transfer.machine";
import { syncMachine as remoteSyncMachine } from "../sync/sync.machine";
import { cloneDocumentMachine } from "./clone-document.machine";
import { deleteDocumentMachine } from "./delete-document.machine";

const logger = new Logger("document.machine");

const loadDocumentMetadataFromDisk = fromPromise<
	DocumentMetadata | undefined,
	{ documentPath: string }
>(({ input }) => filesystemApi.document.metadata.read(input.documentPath));

const loadDocumentMetadataFromRemote = fromPromise<
	DocumentMetadata,
	{ workspaceName: string; documentId: string }
>(async ({ input }) => giteaApi.document.metadata.read(input.workspaceName, input.documentId));

const persistDocumentMetadata = fromPromise<
	void,
	{ documentPath: string; metadata: DocumentMetadata }
>(({ input }) => filesystemApi.document.metadata.write(input.documentPath, input.metadata));

export function workspaceNameFromDocumentListItem(listItem: DocumentListItem) {
	return listItem.workspaces[0]?.name ?? undefined;
}

type DocumentMachineContext = {
	listItem: DocumentListItem;
	metadata?: DocumentMetadata;
	authenticationActorRef: ActorRefFrom<AuthenticationMachine>;
	baseActorRef?: ActorRefFrom<typeof baseMachine>;
	pageActorRefs: Record<string, ActorRefFrom<typeof pageMachine>>;
	gitRepositoryActorRef?: ActorRefFrom<typeof gitRepositoryMachine>;
	remoteSyncActorRef?: ActorRefFrom<typeof remoteSyncMachine>;
	sharingActorRef?: ActorRefFrom<typeof sharingMachine>;
	transferActorRef?: ActorRefFrom<typeof transferMachine>;
};

type DocumentMachineInput = {
	listItem: DocumentListItem;
	authenticationActorRef: ActorRefFrom<AuthenticationMachine>;
};

type DocumentMachineEvent =
	| { type: "document.opened" }
	| { type: "document.clone" }
	| { type: "document.deleted" }
	| { type: "document.closed" }
	| { type: "document.listItemChanged"; payload: { listItem: DocumentListItem } }
	| { type: "document.nameChanged"; payload: { newName: string } }
	| {
			type: "document.publishSettingsChanged";
			payload: { publishSettings: PublishSettings };
	  }
	| {
			type: "document.baseSpecChanged";
			payload: { base: BaseSpec };
	  }
	| {
			type: "pages.reordered";
			payload: { reorderInfo: { pageId: string; parentPageID: string | null }[] };
	  }
	| { type: "page.nameChanged"; payload: { pageId: string; newName: string } }
	| { type: "page.changesPersisted"; payload: { pageId: string; documentId: string } }
	| { type: "page.added"; payload?: { pageMetadata: PageMetadata } }
	| { type: "page.archived"; payload: { pageId: string } }
	| { type: "page.unarchived"; payload: { pageId: string } }
	| { type: "git.branchSwitched" };

type DocumentMachineEmitted = { type: "page.added"; payload: { pageMetadata: PageMetadata } };

export const documentMachine = setup({
	types: {
		context: {} as DocumentMachineContext,
		input: {} as DocumentMachineInput,
		events: {} as DocumentMachineEvent,
		emitted: {} as DocumentMachineEmitted,
	},
	actors: {
		loadDocumentMetadataFromDisk,
		loadDocumentMetadataFromRemote,
		cloneDocument: cloneDocumentMachine,
		persistDocumentMetadata,
		deleteDocument: deleteDocumentMachine,
		createPage: createPageMachine,
	},
	guards: {
		isDesktopAppMode: () => isDesktopAppMode(),

		documentExistsOnDisk: ({ context }) => {
			return context.listItem.path !== undefined;
		},

		userIsAuthorizedToReadDocument: () => {
			// TODO: read permission check
			return true;
		},
		userIsAuthorizedToEditDocument: () => {
			// TODO: write permission check
			return true;
		},

		documentShouldBeRenamedOnPageNameChange: ({ context }) => {
			const documentHasOnePage = context.metadata?.pagesMetadata.length === 1;
			const documentHasNoTitle = isEmpty(context.listItem.title);

			return documentHasOnePage && documentHasNoTitle;
		},
	},
	actions: {
		updateDocumentList: sendParent({
			type: "documentList.refetch",
		}),

		spawnBaseActor: assign(({ context, spawn }) => {
			const { id: documentId, path, workspaces } = context.listItem;

			if (!documentId || !path) {
				return {};
			}

			const baseActorRef = spawn(baseMachine, {
				input: {
					workspaceName: workspaces[0]?.name,
					documentId,
					documentPath: path,
					baseSpec: context.metadata?.base,
				},
				systemId: `base:${documentId}`,
			});

			baseActorRef.send({ type: "base.opened" });

			return { baseActorRef };
		}),

		stopBaseActor: enqueueActions(({ context, enqueue }) => {
			if (context.baseActorRef) {
				enqueue.stopChild(context.baseActorRef);
			}

			enqueue.assign({ baseActorRef: undefined });
		}),

		spawnGitActor: assign(({ context, spawn }) => {
			const { id: documentId, path } = context.listItem;

			if (!documentId || !path) {
				return {};
			}

			return {
				gitRepositoryActorRef: spawn(gitRepositoryMachine, {
					input: {
						path,
					},
					systemId: `git-repository:${documentId}`,
				}),
			};
		}),

		stopGitActor: enqueueActions(({ context, enqueue }) => {
			if (context.gitRepositoryActorRef) {
				enqueue.stopChild(context.gitRepositoryActorRef);
			}

			enqueue.assign({
				gitRepositoryActorRef: undefined,
			});
		}),

		spawnSharingActor: assign(({ context, spawn }) => {
			const { id: documentId } = context.listItem;

			const workspace = workspaceNameFromDocumentListItem(context.listItem) ?? "";
			return {
				sharingActorRef: spawn(sharingMachine, {
					input: {
						workspace,
						documentId,
						authenticationActorRef: context.authenticationActorRef,
						gitRef: context.gitRepositoryActorRef!,
					},
					systemId: `sharing:${documentId}`,
				}),
			};
		}),

		spawnTransferActor: assign(({ context, spawn }) => {
			const { id: documentId } = context.listItem;

			const workspace = workspaceNameFromDocumentListItem(context.listItem) ?? "";

			return {
				transferActorRef: spawn(transferMachine, {
					input: {
						workspace,
						documentId,
						authenticationActorRef: context.authenticationActorRef,
						gitRef: context.gitRepositoryActorRef!,
					},
					systemId: `transfer:${documentId}`,
				}),
			};
		}),

		stopSharingMachine: enqueueActions(({ context, enqueue }) => {
			if (context.sharingActorRef) {
				enqueue.stopChild(context.sharingActorRef);
			}

			enqueue.assign({
				sharingActorRef: undefined,
			});
		}),

		spawnRemoteSyncMachine: assign(({ context, spawn }) => {
			const { id: documentId, path: path } = context.listItem;

			const workspaceName = workspaceNameFromDocumentListItem(context.listItem) ?? "";

			return {
				remoteSyncActorRef: spawn(remoteSyncMachine, {
					input: {
						workspaceName,
						documentId,
						documentPath: path || "",

						authenticationActorRef: context.authenticationActorRef,
						gitRef: context.gitRepositoryActorRef!,
					},
					systemId: `remote-sync:${documentId}`,
				}),
			};
		}),

		stopRemoteSyncMachine: enqueueActions(({ context, enqueue }) => {
			if (context.remoteSyncActorRef) {
				enqueue.stopChild(context.remoteSyncActorRef);
			}

			enqueue.assign({
				remoteSyncActorRef: undefined,
			});
		}),

		spawnPageMachines: assign(({ context, spawn }) => {
			const pageActorRefs = {};

			context.metadata?.pagesMetadata.forEach((pageMetadata) => {
				const pageActorRef = spawn(pageMachine, {
					systemId: `page:${pageMetadata.id}`,
					input: {
						workspaceName: workspaceNameFromDocumentListItem(context.listItem),
						documentId: context.listItem.id,
						pageId: pageMetadata.id,
						documentPath: context.listItem.path ?? undefined,
					},
				});
				pageActorRefs[pageMetadata.id] = pageActorRef;
			});

			return { pageActorRefs };
		}),

		updateGitStatus: enqueueActions(({ context, enqueue }) => {
			if (!context.gitRepositoryActorRef) {
				return;
			}

			enqueue.sendTo(context.gitRepositoryActorRef, {
				type: "git.status",
			});
		}),

		updateRemoteSyncStatus: enqueueActions(({ context, enqueue }) => {
			if (!context.remoteSyncActorRef) {
				return;
			}

			enqueue.sendTo(context.remoteSyncActorRef, {
				type: "sync.regeneratePlan",
			});
		}),

		updateWorkspace: enqueueActions(({ context, enqueue, event }) => {
			if (event.type !== "document.listItemChanged") {
				return;
			}

			const workspaces = event.payload.listItem.workspaces;
			if (
				context.sharingActorRef &&
				workspaces.length > 0 &&
				workspaces[0]?.name !== undefined
			) {
				enqueue.sendTo(context.sharingActorRef, {
					type: "sharing.attachWorkspace",
					workspace: workspaces[0].name,
				});
			}

			if (
				context.remoteSyncActorRef &&
				workspaces.length > 0 &&
				workspaces[0]?.name !== undefined
			) {
				enqueue.sendTo(context.remoteSyncActorRef, {
					type: "sync.resyncWorkspace",
					workspace: workspaces[0].name,
				});
			}
		}),

		renameDocument: enqueueActions(({ context, enqueue, event }) => {
			if (event.type !== "document.nameChanged" && event.type !== "page.nameChanged") {
				return;
			}

			const { newName: title } = event.payload;

			const listItem = { ...context.listItem, title };
			const metadata = { ...context.metadata!, title };

			enqueue.assign({
				listItem,
				metadata,
			});

			enqueue.sendParent({
				type: "documentList.updated",
				payload: { listItems: [listItem] },
			});

			enqueue(
				() =>
					void invoke("update_recent_docs", {
						removeItem: false,
						recentDoc: {
							title: context.listItem.title,
							workspaceName:
								workspaceNameFromDocumentListItem(context.listItem) ?? "local",
							documentId: context.listItem.id,
						},
					})
			);
		}),

		renamePage: assign(({ context, event }) => {
			if (event.type !== "page.nameChanged") {
				return {};
			}

			return produce(context, (draft) => {
				const pageMetadata = draft.metadata?.pagesMetadata.find(
					(pageMetadata) => pageMetadata.id === event.payload.pageId
				);
				if (pageMetadata) {
					pageMetadata.title = event.payload.newName;
				}
			});
		}),

		updateDocumentBaseSpec: assign(({ context, event }) => {
			if (event.type !== "document.baseSpecChanged") {
				return {};
			}

			return produce(context, (draft) => {
				if (draft.metadata) {
					draft.metadata.base = event.payload.base;
				}
			});
		}),
	},
}).createMachine({
	id: "document",
	context: ({ input }) => {
		return {
			listItem: input.listItem,
			authenticationActorRef: input.authenticationActorRef,
			pageActorRefs: {},
		};
	},
	initial: "idle",
	states: {
		idle: {
			on: {
				"document.opened": {
					entry: [
						({ context, event }) => {
							console.log("document opened", context, event);
						},
					],
					guard: "userIsAuthorizedToReadDocument",
					target: "fetching",
				},
			},
		},

		fetching: {
			description: "Fetching the document metadata",
			initial: "idle",
			states: {
				idle: {
					always: [
						{
							guard: "documentExistsOnDisk",
							target: "fetchingFromDisk",
						},
						{
							target: "fetchingFromRemote",
						},
					],
				},
				fetchingFromDisk: {
					invoke: {
						src: "loadDocumentMetadataFromDisk",
						input: ({ context }) => ({
							documentPath: context.listItem.path!,
						}),
						onDone: {
							actions: [
								assign({
									metadata: ({ event }) => event.output,
								}),
							],
							target: "spawningChildren",
						},
					},
				},
				fetchingFromRemote: {
					invoke: {
						src: "loadDocumentMetadataFromRemote",
						input: ({ context }) => ({
							workspaceName: workspaceNameFromDocumentListItem(context.listItem)!,
							documentId: context.listItem.id,
						}),
						onDone: {
							actions: assign({
								metadata: ({ event }) => event.output,
							}),
							target: "spawningChildren",
						},
					},
				},
				spawningChildren: {
					entry: ["spawnGitActor", "spawnPageMachines", "spawnBaseActor"],
					always: {
						target: "spawningChildrenWithDependencies",
					},
				},
				spawningChildrenWithDependencies: {
					entry: ["spawnRemoteSyncMachine", "spawnSharingActor", "spawnTransferActor"],
					always: {
						target: "done",
					},
				},
				done: {
					type: "final",
				},
			},
			onDone: [
				// If the document doesn't exist on disk, we need to clone it
				{
					guard: or([not("isDesktopAppMode"), not("documentExistsOnDisk")]),
					target: "viewing",
				},

				// If the user is authorized to edit the document, we can automatically
				// transition to the editing state
				{
					guard: "userIsAuthorizedToEditDocument",
					target: "editing",
				},
			],
		},

		viewing: {
			on: {
				"document.clone": {
					target: "cloning",
				},
			},
		},

		cloning: {
			invoke: {
				src: "cloneDocument",
				input: ({ context }) => ({
					workspaceName: workspaceNameFromDocumentListItem(context.listItem)!,
					documentId: context.listItem.id,
				}),
				onDone: {
					actions: assign(({ context, event }) => ({
						listItem: {
							...context.listItem,
							path: event.output.path,
						},
					})),
					target: "editing",
				},
				onError: {
					target: "error",
				},
			},
			// after cloning, we need to update the document list and restart the git machine
			exit: ["updateDocumentList", "stopGitActor", "spawnGitActor", "spawnBaseActor"],
		},

		editing: {
			description: "Editing the document in the app",
			type: "parallel",
			entry: ({ context }) =>
				void invoke("update_recent_docs", {
					removeItem: false,
					recentDoc: {
						title: context.listItem.title,
						workspaceName:
							workspaceNameFromDocumentListItem(context.listItem) ?? "local",
						documentId: context.listItem.id,
					},
				}),
			states: {
				persistence: {
					initial: "idle",
					states: {
						idle: {},
						debouncing: {
							after: {
								1000: "persisting",
							},
						},
						persisting: {
							invoke: {
								src: "persistDocumentMetadata",
								input: ({ context }) => ({
									documentPath: context.listItem.path!,
									metadata: context.metadata!,
								}),
								onDone: {
									target: "idle",
								},
								onError: {
									actions: ({ event }) => {
										logger.error("Error persisting document metadata", {
											cause: event.error,
										});
									},
									target: "#document.error",
								},
							},
							exit: ["updateGitStatus", "updateRemoteSyncStatus"],
						},
						addingPage: {
							invoke: {
								src: "createPage",
								input: ({ context }) => ({
									workspaceName: workspaceNameFromDocumentListItem(
										context.listItem
									)!,
									documentId: context.listItem.id,
									documentPath: context.listItem.path!,
								}),
								onDone: {
									actions: [
										assign(({ context, event, spawn }) => ({
											metadata: {
												...context.metadata!,
												pagesMetadata: [
													...context.metadata!.pagesMetadata,
													event.output.pageMetadata,
												],
											},
											pageActorRefs: {
												...context.pageActorRefs,
												[event.output.pageMetadata.id]: spawn(pageMachine, {
													systemId: `page:${event.output.pageMetadata.id}`,
													input: {
														workspaceName:
															workspaceNameFromDocumentListItem(
																context.listItem
															),
														documentId: context.listItem.id,
														pageId: event.output.pageMetadata.id,
														documentPath: context.listItem.path!,
													},
												}),
											},
										})),
										emit(({ event }) => ({
											type: "page.added",
											payload: {
												pageMetadata: event.output.pageMetadata,
											},
										})),
									],
									target: "#document.editing.persistence.debouncing",
								},
								onError: {
									actions: ({ event }) => {
										logger.error("Error creating page", {
											cause: event.error,
										});
									},
									target: "#document.editing.persistence.idle",
								},
							},
						},
					},
				},
			},
			on: {
				"document.publishSettingsChanged": {
					actions: assign(({ context, event }) => ({
						metadata: {
							...context.metadata!,
							publishSettings: event.payload.publishSettings,
						},
					})),
					target: "editing.persistence.debouncing",
				},
				"page.added": {
					target: "editing.persistence.addingPage",
				},
				"page.nameChanged": [
					{
						guard: "documentShouldBeRenamedOnPageNameChange",
						actions: ["renamePage", "renameDocument"],
						target: "editing.persistence.debouncing",
					},
					{
						actions: "renamePage",
						target: "editing.persistence.debouncing",
					},
				],
				"document.baseSpecChanged": {
					actions: "updateDocumentBaseSpec",
					target: "editing.persistence.debouncing",
				},
				"pages.reordered": {
					actions: assign(({ context, event }) =>
						produce(context, (draft) => {
							const sortedPages = event.payload.reorderInfo.reduce<
								DocumentMetadata["pagesMetadata"]
							>((acc, { pageId, parentPageID }, index) => {
								const page = draft.metadata?.pagesMetadata.find(
									(page) => page.id === pageId
								);
								if (page) {
									const newPage = { ...page, parentPageID };
									if (index === 0) {
										newPage.slug = "index";
									} else if (page.slug === "index") {
										newPage.slug = page.id;
									}
									acc.push(newPage);
								}
								return acc;
							}, []);

							draft.metadata!.pagesMetadata = sortedPages;
						})
					),
					target: "editing.persistence.debouncing",
				},
				"page.archived": {
					actions: assign(({ context, event }) =>
						produce(context, (draft) => {
							if (!draft.metadata) {
								return;
							}

							const pageMetadata = draft.metadata.pagesMetadata.find(
								(page) => page.id === event.payload.pageId
							);
							if (!pageMetadata) {
								return;
							}

							pageMetadata.archived = true;
						})
					),
					target: "editing.persistence.debouncing",
				},
				"page.unarchived": {
					actions: assign(({ context, event }) =>
						produce(context, (draft) => {
							if (!draft.metadata) {
								return;
							}

							const pageMetadata = draft.metadata.pagesMetadata.find(
								(page) => page.id === event.payload.pageId
							);
							if (!pageMetadata) {
								return;
							}

							pageMetadata.archived = false;
						})
					),
					target: "editing.persistence.debouncing",
				},
				"document.nameChanged": {
					actions: "renameDocument",
					target: "editing.persistence.debouncing",
				},
				"document.deleted": {
					target: "deleting",
				},
			},
		},

		deleting: {
			invoke: {
				src: "deleteDocument",
				input: ({ context }) => ({
					documentId: context.listItem.id,
					workspaces: context.listItem.workspaces,
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Error deleting document", {
							cause: event.error,
						});
					},
				},
				onDone: {
					actions: [
						({ context }) =>
							void invoke("update_recent_docs", {
								removeItem: true,
								recentDoc: {
									title: context.listItem.title,
									workspaceName:
										workspaceNameFromDocumentListItem(context.listItem) ??
										"local",
									documentId: context.listItem.id,
								},
							}),
						sendParent(({ context }) => ({
							type: "document.deleted",
							payload: {
								documentId: context.listItem.id,
							},
						})),
					],
				},
			},
		},

		error: {
			on: {
				"document.opened": {
					description: "Retry loading the document content",
					target: "fetching",
				},
			},
		},
	},

	on: {
		"page.changesPersisted": {
			actions: ["updateGitStatus", "updateRemoteSyncStatus"],
		},
		"git.branchSwitched": {
			actions: [
				"updateRemoteSyncStatus",
				enqueueActions(({ context, enqueue }) => {
					enqueue.sendParent({
						type: "document.reload",
						payload: { listItem: context.listItem },
					});
				}),
			],
		},
		"document.listItemChanged": {
			actions: [
				assign(({ event }) => ({
					listItem: event.payload.listItem,
				})),
				"updateWorkspace",
				"updateGitStatus",
			],
		},
		"document.closed": {
			description: "Close the document and clear its data out of the machine",
			target: ".idle",
			actions: [
				"stopRemoteSyncMachine", // TODO: race conditions?
				"stopGitActor",
				"stopSharingMachine",
			],
		},
	},
});
